// MCHD Room Scheduler - Modern Holiday Calendar Component
// Advanced calendar component for holiday visualization and management

import { useState, useMemo } from 'react';
import {
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Stack,
  ActionIcon,
  Tooltip,
  Box,
  Loader,
  Alert,
  Modal,
  TextInput,
  Select,
  Flex,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCalendar,
  IconPlus,
  IconBuilding,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useHolidayCalendar, useBuildings } from '../../hooks/admin/useHolidays';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isWeekend, addMonths, subMonths, getDay, addDays } from 'date-fns';

// =============================================================================
// TYPES
// =============================================================================

interface HolidayCalendarProps {
  selectedBuildingId?: string;
  onHolidayClick?: (holiday: any) => void;
  onDateClick?: (date: Date) => void;
  showActions?: boolean;
  className?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  holidays: any[];
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Modern Holiday Calendar Component
 * Features:
 * - Beautiful calendar grid with modern design
 * - Holiday visualization with color coding
 * - Smooth animations and transitions
 * - Responsive design for all screen sizes
 * - Quick holiday creation
 * - Building selection
 * - Interactive date selection
 */
export function HolidayCalendar({
  selectedBuildingId,
  onHolidayClick,
  onDateClick,
  showActions = true,
  className,
}: HolidayCalendarProps): JSX.Element {
  // State management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(
    selectedBuildingId || null
  );
  
  // Modal states
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [selectedHoliday, setSelectedHoliday] = useState<any>(null);

  // Hooks
  const { data: buildingsData, isLoading: buildingsLoading } = useBuildings();
  const { data: calendarData, isLoading: calendarLoading } = useHolidayCalendar(
    selectedBuilding || '',
    currentDate.getFullYear(),
    currentDate.getMonth() + 1
  );

  // Form for creating/editing holidays
  const form = useForm({
    initialValues: {
      buildingId: selectedBuilding || '',
      dateDay: '',
      label: '',
    },
    validate: {
      buildingId: (value: string) => (!value ? 'Building is required' : null),
      dateDay: (value: string) => (!value ? 'Date is required' : null),
      label: (value: string) => (!value ? 'Label is required' : null),
    },
  });

  // Calendar generation logic
  const calendarDays = useMemo((): CalendarDay[] => {
    // Always generate calendar when building is selected, even if no holidays
    if (!selectedBuilding) return [];

    // Use current date if no calendar data yet
    const year = calendarData?.data?.year || currentDate.getFullYear();
    const month = calendarData?.data?.month || currentDate.getMonth() + 1;
    const holidays = calendarData?.data?.holidays || [];
    const startDate = startOfMonth(new Date(year, month - 1, 1));
    
    // Get all days in the month view (including previous/next month days)
    const monthStart = startOfMonth(startDate);
    const monthEnd = endOfMonth(startDate);
    
    // Calculate the start of the calendar grid (Sunday of the first week)
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - getDay(monthStart));
    
    // Calculate the end of the calendar grid (Saturday of the last week)
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(calendarEnd.getDate() + (6 - getDay(monthEnd)));
    
    // Ensure we have exactly 42 days (6 weeks × 7 days)
    const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    
    // If we don't have exactly 42 days, adjust the end date
    if (allDays.length !== 42) {
      const targetEnd = addDays(calendarStart, 41); // 42 days total (0-41)
      const adjustedDays = eachDayOfInterval({ start: calendarStart, end: targetEnd });
      return adjustedDays.map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayHolidays = holidays.filter(holiday => 
          holiday.dateDay.split('T')[0] === dateStr
        );
        
        return {
          date,
          isCurrentMonth: date.getMonth() === month - 1,
          isToday: isToday(date),
          isWeekend: isWeekend(date),
          holidays: dayHolidays,
        };
      });
    }
    
    // Ensure exactly 42 days (6 weeks × 7 days) for perfect grid
    const days = allDays.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayHolidays = holidays.filter(holiday => 
        holiday.dateDay.split('T')[0] === dateStr
      );
      
      return {
        date,
        isCurrentMonth: date.getMonth() === month - 1,
        isToday: isToday(date),
        isWeekend: isWeekend(date),
        holidays: dayHolidays,
      };
    });

    // Ensure we have exactly 42 days for a complete 6x7 grid
    if (days.length !== 42) {
      console.warn(`Calendar should have exactly 42 days, but got ${days.length}`);
      // If we don't have 42 days, pad with additional days
      const lastDay = days[days.length - 1];
      const additionalDays = [];
      for (let i = days.length; i < 42; i++) {
        const newDate = addDays(lastDay.date, i - days.length + 1);
        additionalDays.push({
          date: newDate,
          isCurrentMonth: false,
          isToday: false,
          isWeekend: isWeekend(newDate),
          holidays: [],
        });
      }
      return [...days, ...additionalDays];
    }

    return days;
  }, [calendarData, selectedBuilding, currentDate]);

  // Event handlers with smooth transitions
  const handlePreviousMonth = () => {
    setCurrentDate(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => addMonths(prev, 1));
  };

  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    if (onDateClick) {
      onDateClick(date);
    }
  };

  const handleHolidayClick = (holiday: any) => {
    if (onHolidayClick) {
      onHolidayClick(holiday);
    }
    setSelectedHoliday(holiday);
    openEditModal();
  };

  const handleCreateHoliday = (_values: typeof form.values) => {
    // This will be handled by the parent component
    closeCreateModal();
    form.reset();
    notifications.show({
      title: 'Holiday Created',
      message: 'The holiday has been created successfully.',
      color: 'green',
    });
  };

  const handleEditHoliday = (_values: typeof form.values) => {
    // This will be handled by the parent component
    closeEditModal();
    form.reset();
    notifications.show({
      title: 'Holiday Updated',
      message: 'The holiday has been updated successfully.',
      color: 'blue',
    });
  };

  // Loading state - only show loading for buildings, not calendar data
  if (buildingsLoading) {
    return (
      <Paper radius="lg" p="xl" {...(className && { className })}>
        <Flex align="center" justify="center" h={400}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading calendar...</Text>
          </Stack>
        </Flex>
      </Paper>
    );
  }

  // Error state - only show when no building selected
  if (!selectedBuilding) {
    return (
      <Paper radius="lg" p="xl" {...(className && { className })}>
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Calendar Unavailable"
          color="orange"
        >
          Please select a building to view the holiday calendar.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper 
      radius="lg" 
      p="xl" 
      shadow="sm" 
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid var(--mantine-color-gray-2)',
        position: 'relative',
        overflow: 'hidden',
      }}
      {...(className && { className })}
    >
      {/* Subtle background pattern */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />
      
      {/* Header */}
      <Stack gap="lg" style={{ position: 'relative', zIndex: 1 }}>
        {/* Calendar Header */}
        <Group justify="space-between" align="center">
          <Group gap="md">
            <IconCalendar size={24} color="var(--mantine-color-blue-6)" />
            <Stack gap={0}>
              <Group gap="xs" align="center">
                <Text size="xl" fw={600}>
                  {format(currentDate, 'MMMM yyyy')}
                </Text>
                {calendarLoading && (
                  <Loader 
                    size="sm" 
                    style={{
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                )}
              </Group>
              <Text size="sm" c="dimmed">
                Holiday Calendar
              </Text>
            </Stack>
          </Group>
          
          <Group gap="sm">
            <Select
              placeholder="Select Building"
              data={buildingsData?.success ? 
                buildingsData.data?.map(b => ({ 
                  value: b.id, 
                  label: b.name 
                })) || [] 
                : []
              }
              value={selectedBuilding}
              onChange={(value) => setSelectedBuilding(value)}
              leftSection={<IconBuilding size={16} />}
              size="sm"
              w={200}
            />
            
            {showActions && (
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={openCreateModal}
                size="sm"
                variant="light"
              >
                Add Holiday
              </Button>
            )}
          </Group>
        </Group>

        {/* Navigation */}
        <Group justify="space-between" align="center">
          <ActionIcon
            variant="light"
            size="lg"
            onClick={handlePreviousMonth}
            aria-label="Previous month"
            style={{
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(-2px)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)';
            }}
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
          
          <Button
            variant="subtle"
            onClick={handleTodayClick}
            size="sm"
            leftSection={<IconCalendar size={14} />}
            style={{
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Today
          </Button>
          
          <ActionIcon
            variant="light"
            size="lg"
            onClick={handleNextMonth}
            aria-label="Next month"
            style={{
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)';
            }}
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>

        {/* Calendar Grid */}
        <Box>
          {/* Day headers - Perfectly aligned with calendar grid */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '6px',
              marginBottom: '8px',
              padding: '0 8px',
            }}
          >
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Box
                key={day}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 4px',
                  height: '32px',
                }}
              >
                <Text 
                  size="sm" 
                  fw={700} 
                  c="var(--mantine-color-gray-7)"
                  style={{
                    textAlign: 'center',
                    width: '100%',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                  }}
                >
                  {day}
                </Text>
              </Box>
            ))}
          </Box>

          {/* Calendar days - Proper 6x7 grid structure */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridTemplateRows: 'repeat(6, 1fr)',
              gap: '6px',
              minHeight: '420px',
              transition: 'all 0.3s ease',
              animation: 'fadeIn 0.4s ease-out',
              padding: '8px',
              borderRadius: 'var(--mantine-radius-lg)',
              backgroundColor: 'var(--mantine-color-gray-0)',
            }}
          >
            {calendarDays.map((day, index) => (
              <CalendarDay
                key={index}
                day={day}
                selectedDate={selectedDate}
                onDateClick={handleDateClick}
                onHolidayClick={handleHolidayClick}
              />
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Group gap="lg" justify="center">
          <Group gap="xs">
            <Box w={12} h={12} bg="blue.3" style={{ borderRadius: '4px' }} />
            <Text size="xs" c="dimmed">Holiday</Text>
          </Group>
          <Group gap="xs">
            <Box w={12} h={12} bg="gray.3" style={{ borderRadius: '4px' }} />
            <Text size="xs" c="dimmed">Weekend</Text>
          </Group>
          <Group gap="xs">
            <Box w={12} h={12} bg="red.3" style={{ borderRadius: '4px' }} />
            <Text size="xs" c="dimmed">Today</Text>
          </Group>
        </Group>
      </Stack>

      {/* Create Holiday Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title="Create Holiday"
        size="md"
      >
        <form onSubmit={form.onSubmit(handleCreateHoliday)}>
          <Stack gap="md">
            <Select
              label="Building"
              placeholder="Select building"
              data={buildingsData?.success ? 
                buildingsData.data?.map(b => ({ 
                  value: b.id, 
                  label: b.name 
                })) || [] 
                : []
              }
              {...form.getInputProps('buildingId')}
              required
            />
            
            <TextInput
              label="Date"
              placeholder="YYYY-MM-DD"
              type="date"
              {...form.getInputProps('dateDay')}
              required
            />
            
            <TextInput
              label="Holiday Label"
              placeholder="Enter holiday name"
              {...form.getInputProps('label')}
              required
            />
            
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button type="submit">
                Create Holiday
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Holiday Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title="Edit Holiday"
        size="md"
      >
        {selectedHoliday && (
          <form onSubmit={form.onSubmit(handleEditHoliday)}>
            <Stack gap="md">
              <TextInput
                label="Date"
                placeholder="YYYY-MM-DD"
                type="date"
                defaultValue={selectedHoliday.dateDay}
                {...form.getInputProps('dateDay')}
                required
              />
              
              <TextInput
                label="Holiday Label"
                placeholder="Enter holiday name"
                defaultValue={selectedHoliday.label}
                {...form.getInputProps('label')}
                required
              />
              
              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button type="submit">
                  Update Holiday
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>
    </Paper>
  );
}

// =============================================================================
// CALENDAR DAY COMPONENT
// =============================================================================

interface CalendarDayProps {
  day: CalendarDay;
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
  onHolidayClick: (holiday: any) => void;
}

/**
 * Individual calendar day component with modern styling
 */
function CalendarDay({ day, selectedDate, onDateClick, onHolidayClick }: CalendarDayProps): JSX.Element {
  const isSelected = selectedDate && isSameDay(day.date, selectedDate);
  
  return (
    <Box
      style={{
        height: '80px',
        padding: '8px',
        border: isSelected 
          ? '2px solid var(--mantine-color-blue-6)' 
          : day.isToday 
          ? '2px solid var(--mantine-color-blue-4)'
          : '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: day.isToday 
          ? 'var(--mantine-color-blue-1)' 
          : day.isCurrentMonth
          ? 'white'
          : 'var(--mantine-color-gray-1)',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        opacity: day.isCurrentMonth ? 1 : 0.5,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: day.isToday 
          ? '0 2px 8px rgba(34, 139, 34, 0.15)' 
          : '0 1px 3px rgba(0, 0, 0, 0.05)',
        width: '100%',
        minHeight: '80px',
      }}
      onClick={() => onDateClick(day.date)}
      onMouseEnter={(e) => {
        if (!day.isCurrentMonth) return;
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 139, 34, 0.2)';
        e.currentTarget.style.borderColor = 'var(--mantine-color-blue-5)';
      }}
      onMouseLeave={(e) => {
        if (!day.isCurrentMonth) return;
        e.currentTarget.style.backgroundColor = day.isToday 
          ? 'var(--mantine-color-blue-1)' 
          : 'white';
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = day.isToday 
          ? '0 2px 8px rgba(34, 139, 34, 0.15)' 
          : '0 1px 3px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.borderColor = day.isToday 
          ? 'var(--mantine-color-blue-4)'
          : 'var(--mantine-color-gray-3)';
      }}
    >
      <Stack gap="xs" h="100%">
        {/* Date number */}
        <Text
          size="sm"
          fw={day.isToday ? 700 : day.isCurrentMonth ? 600 : 400}
          c={day.isToday 
            ? 'var(--mantine-color-blue-7)' 
            : day.isCurrentMonth 
            ? 'dark' 
            : 'var(--mantine-color-gray-5)'
          }
          ta="center"
          style={{
            fontSize: day.isToday ? '14px' : '13px',
          }}
        >
          {format(day.date, 'd')}
        </Text>
        
        {/* Holidays */}
        <Stack gap={2} style={{ flex: 1 }}>
          {day.holidays.slice(0, 2).map((holiday, _index) => (
            <Tooltip key={holiday.id} label={holiday.label} position="top">
              <Badge
                size="xs"
                color="blue"
                variant="filled"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onHolidayClick(holiday);
                }}
              >
                {holiday.label.length > 8 ? `${holiday.label.slice(0, 8)}...` : holiday.label}
              </Badge>
            </Tooltip>
          ))}
          
          {day.holidays.length > 2 && (
            <Badge size="xs" color="gray" variant="light">
              +{day.holidays.length - 2}
            </Badge>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
